/**
 * CashFlowDetailModal Component
 * Detailed cash flow forecast drill-down with methodology explanation,
 * scenario bands, per-invoice breakdown, and client assumptions.
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import { fetchDetailedCashflow } from '@/api/metricsApi';
import { formatCurrency, formatDate } from '@/utils/formatters';
import { StatusBadge } from './StatusBadge';
import { SectionCard } from './SectionCard';
import { SortableHeader } from './SortableHeader';
import { useTableSort } from '@/hooks/useTableSort';
import type {
  CashFlowForecast,
  ForecastLineItem,
  ForecastConfidence,
} from '@/types/metrics';
import './InvoiceListModal.css';

interface CashFlowDetailModalProps {
  onClose: () => void;
}

function confidenceToStatus(c: ForecastConfidence): 'healthy' | 'warning' | 'critical' {
  if (c === 'high') return 'healthy';
  if (c === 'medium') return 'warning';
  return 'critical';
}

export function CashFlowDetailModal({ onClose }: CashFlowDetailModalProps) {
  const [forecast, setForecast] = useState<CashFlowForecast | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const modalRef = useRef<HTMLDivElement>(null);
  const previousFocusRef = useRef<HTMLElement | null>(null);

  const loadData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await fetchDetailedCashflow();
      setForecast(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load forecast');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadData(); }, [loadData]);

  useEffect(() => {
    previousFocusRef.current = document.activeElement as HTMLElement;
    modalRef.current?.focus();
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = '';
      previousFocusRef.current?.focus();
    };
  }, []);

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [onClose]);

  const handleBackdropClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) onClose();
  };

  return (
    <div className="invoice-modal-overlay" onClick={handleBackdropClick} role="presentation">
      <div
        className="invoice-modal"
        role="dialog"
        aria-modal="true"
        aria-label="Cash Flow Forecast Detail"
        ref={modalRef}
        tabIndex={-1}
      >
        <div className="invoice-modal__header">
          <h2 className="invoice-modal__title">Cash Flow Forecast</h2>
          <button className="invoice-modal__close" onClick={onClose} aria-label="Close modal">
            &times;
          </button>
        </div>

        {loading && (
          <div className="invoice-modal__loading">
            <div className="loading-spinner" />
            <p>Loading forecast detail...</p>
          </div>
        )}

        {error && (
          <div className="invoice-modal__error">
            <p>{error}</p>
            <button className="invoice-modal__retry" onClick={loadData}>Retry</button>
          </div>
        )}

        {!loading && !error && forecast && (
          <div className="invoice-modal__body" style={{ padding: '0 16px 16px' }}>
            <MethodologyBanner text={forecast.methodology} />
            {forecast.scenarios && <ScenarioSummary scenarios={forecast.scenarios} />}
            <WaterfallBreakdown forecast={forecast} />
            {forecast.lineItems && forecast.lineItems.length > 0 && (
              <InvoiceBreakdown lineItems={forecast.lineItems} />
            )}
            {forecast.clientAssumptions && forecast.clientAssumptions.length > 0 && (
              <ClientAssumptions assumptions={forecast.clientAssumptions} />
            )}
          </div>
        )}
      </div>
    </div>
  );
}

function MethodologyBanner({ text }: { text?: string }) {
  if (!text) return null;
  return (
    <div style={{
      background: 'var(--pale-blue, #E5EEEF)',
      borderRadius: '8px',
      padding: '12px 16px',
      marginBottom: '16px',
      fontSize: '13px',
      lineHeight: '1.5',
      color: 'var(--navy, #17232D)',
    }}>
      <strong>How this forecast is calculated:</strong> {text}
    </div>
  );
}

function ScenarioSummary({ scenarios }: {
  scenarios: { optimistic: { day90: number }; expected: { day90: number }; conservative: { day90: number } };
}) {
  return (
    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '12px', marginBottom: '16px' }}>
      <ScenarioCard label="Conservative" value={scenarios.conservative.day90} variant="critical" />
      <ScenarioCard label="Expected" value={scenarios.expected.day90} variant="warning" />
      <ScenarioCard label="Optimistic" value={scenarios.optimistic.day90} variant="healthy" />
    </div>
  );
}

function ScenarioCard({ label, value, variant }: {
  label: string; value: number; variant: 'healthy' | 'warning' | 'critical';
}) {
  const colors = {
    healthy: { bg: '#f0fdf4', border: '#bbf7d0' },
    warning: { bg: '#fffbeb', border: '#fde68a' },
    critical: { bg: '#fef2f2', border: '#fecaca' },
  };
  const c = colors[variant];
  return (
    <div style={{
      background: c.bg, border: `1px solid ${c.border}`,
      borderRadius: '8px', padding: '12px', textAlign: 'center',
    }}>
      <div style={{ fontSize: '12px', color: '#666', marginBottom: '4px' }}>{label}</div>
      <div style={{ fontSize: '20px', fontWeight: 600 }}>{formatCurrency(value)}</div>
      <div style={{ fontSize: '11px', color: '#999' }}>90-day projection</div>
    </div>
  );
}

function WaterfallBreakdown({ forecast }: { forecast: CashFlowForecast }) {
  const inflow90 = forecast.inflows?.day90 ?? 0;
  const outflow90 = forecast.outflows?.day90 ?? 0;
  return (
    <div style={{
      display: 'grid', gridTemplateColumns: '1fr auto 1fr auto 1fr',
      alignItems: 'center', gap: '8px', marginBottom: '16px',
      background: '#f8fafc', borderRadius: '8px', padding: '16px',
    }}>
      <WaterfallItem label="Expected Inflows" value={inflow90} color="var(--success, #2C8248)" />
      <span style={{ fontSize: '20px', color: '#999' }}>-</span>
      <WaterfallItem label="Expected Outflows" value={outflow90} color="var(--error, #dc2626)" />
      <span style={{ fontSize: '20px', color: '#999' }}>=</span>
      <WaterfallItem label="Net Cash Flow" value={forecast.day90} color="var(--marine, #075156)" />
    </div>
  );
}

function WaterfallItem({ label, value, color }: { label: string; value: number; color: string }) {
  return (
    <div style={{ textAlign: 'center' }}>
      <div style={{ fontSize: '11px', color: '#666', marginBottom: '4px' }}>{label}</div>
      <div style={{ fontSize: '18px', fontWeight: 600, color }}>{formatCurrency(value)}</div>
    </div>
  );
}

function InvoiceBreakdown({ lineItems }: { lineItems: ForecastLineItem[] }) {
  const { sorted, sortState, toggleSort } = useTableSort(
    lineItems,
    { key: 'weightedAmount', direction: 'desc' }
  );
  return (
    <SectionCard title="Invoice Breakdown" badge={
      <span style={{ fontSize: '12px', color: '#666' }}>{lineItems.length} invoices</span>
    }>
      <div style={{ overflowX: 'auto' }}>
        <table className="invoice-modal__table">
          <thead>
            <tr>
              <SortableHeader label="Invoice #" sortKey="invoiceNumber" sortState={sortState} onToggle={toggleSort} />
              <SortableHeader label="Client" sortKey="contactName" sortState={sortState} onToggle={toggleSort} />
              <SortableHeader label="Amount Due" sortKey="amountDue" sortState={sortState} onToggle={toggleSort} alignRight />
              <SortableHeader label="Due Date" sortKey="dueDate" sortState={sortState} onToggle={toggleSort} />
              <SortableHeader label="Expected Pay" sortKey="expectedPayDate" sortState={sortState} onToggle={toggleSort} />
              <th>Confidence</th>
              <SortableHeader label="Prob." sortKey="probability" sortState={sortState} onToggle={toggleSort} alignRight />
              <SortableHeader label="Weighted" sortKey="weightedAmount" sortState={sortState} onToggle={toggleSort} alignRight />
              <th>Window</th>
            </tr>
          </thead>
          <tbody>
            {sorted.map((li) => (
              <tr key={li.invoiceID}>
                <td>{li.invoiceNumber}</td>
                <td>{li.contactName}</td>
                <td className="invoice-modal__amount">{formatCurrency(li.amountDue)}</td>
                <td>{formatDate(li.dueDate, 'dd MMM yyyy')}</td>
                <td>{formatDate(li.expectedPayDate, 'dd MMM yyyy')}</td>
                <td>
                  <StatusBadge
                    status={confidenceToStatus(li.confidence)}
                    label={li.confidence}
                    size="sm"
                  />
                </td>
                <td style={{ textAlign: 'right' }}>{Math.round(li.probability * 100)}%</td>
                <td className="invoice-modal__amount">{formatCurrency(li.weightedAmount)}</td>
                <td style={{ fontSize: '12px', color: '#666' }}>
                  {li.forecastWindow === 'beyond' ? '90+' : li.forecastWindow.replace('day', '')}d
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </SectionCard>
  );
}

function ClientAssumptions({ assumptions }: {
  assumptions: Array<{
    contactName: string; avgDelayDays: number;
    sampleSize: number; reliability: ForecastConfidence;
  }>;
}) {
  return (
    <SectionCard title="Client Payment Assumptions">
      <div style={{ overflowX: 'auto' }}>
        <table className="invoice-modal__table">
          <thead>
            <tr>
              <th>Client</th>
              <th style={{ textAlign: 'right' }}>Avg Days Late</th>
              <th style={{ textAlign: 'right' }}>Sample Size</th>
              <th>Reliability</th>
            </tr>
          </thead>
          <tbody>
            {assumptions.map((a) => (
              <tr key={a.contactName}>
                <td>{a.contactName}</td>
                <td style={{ textAlign: 'right' }}>
                  {a.avgDelayDays > 0 ? `+${a.avgDelayDays}` : a.avgDelayDays}
                </td>
                <td style={{ textAlign: 'right' }}>{a.sampleSize} invoices</td>
                <td>
                  <StatusBadge
                    status={confidenceToStatus(a.reliability)}
                    label={a.reliability}
                    size="sm"
                  />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </SectionCard>
  );
}
